<!-- eslint-disable -->
<template>
  <!-- Album Grid Display (for posts) -->
  <div v-if="!lightboxOpen" class="album-grid" :class="gridClass">
    <div 
      v-for="(image, index) in displayImages" 
      :key="index"
      class="album-image"
      :class="{ 'has-overlay': index === 3 && remainingCount > 0 }"
      @click="openLightbox(index)"
    >
      <v-img 
        :src="image" 
        :aspect-ratio="allImages.length === 1 ? undefined : 1"
        :max-height="allImages.length === 1 ? '300px' : undefined"
        cover
        class="rounded cursor-pointer"
      >
        <template v-slot:placeholder>
          <div class="d-flex align-center justify-center fill-height">
            <v-progress-circular indeterminate color="primary" size="24"></v-progress-circular>
          </div>
        </template>
      </v-img>
      <!-- Zoom icon on single images -->
      <div v-if="allImages.length === 1" class="zoom-hint">
        <v-icon color="white" size="small">mdi-magnify-plus-outline</v-icon>
      </div>
      <!-- +X more overlay -->
      <div v-if="index === 3 && remainingCount > 0" class="overlay">
        <span class="overlay-text">+{{ remainingCount }}</span>
      </div>
    </div>
  </div>

  <!-- Lightbox -->
  <v-dialog v-model="lightboxOpen" fullscreen>
    <div class="lightbox" @click.self="closeLightbox">
      <!-- Close button -->
      <v-btn
        icon
        variant="text"
        color="white"
        class="close-btn"
        @click="closeLightbox"
      >
        <v-icon size="large">mdi-close</v-icon>
      </v-btn>

      <!-- Counter -->
      <div class="counter">
        {{ currentIndex + 1 }} / {{ allImages.length }}
      </div>

      <!-- Main image -->
      <div class="main-image-container">
        <!-- Previous button -->
        <v-btn
          v-if="allImages.length > 1"
          icon
          variant="text"
          color="white"
          class="nav-btn nav-prev"
          @click="prevImage"
          :disabled="currentIndex === 0"
        >
          <v-icon size="x-large">mdi-chevron-left</v-icon>
        </v-btn>

        <!-- Image -->
        <div class="main-image" @click.stop>
          <img
            :src="allImages[currentIndex]"
            class="lightbox-img"
            @error="handleImageError"
          />
        </div>

        <!-- Next button -->
        <v-btn
          v-if="allImages.length > 1"
          icon
          variant="text"
          color="white"
          class="nav-btn nav-next"
          @click="nextImage"
          :disabled="currentIndex === allImages.length - 1"
        >
          <v-icon size="x-large">mdi-chevron-right</v-icon>
        </v-btn>
      </div>

      <!-- Thumbnail strip -->
      <div v-if="allImages.length > 1" class="thumbnail-strip">
        <div class="thumbnail-container">
          <div
            v-for="(image, index) in allImages"
            :key="index"
            class="thumbnail"
            :class="{ active: index === currentIndex }"
            @click.stop="currentIndex = index"
          >
            <v-img :src="image" aspect-ratio="1" cover></v-img>
          </div>
        </div>
      </div>
    </div>
  </v-dialog>
</template>

<script setup>
/* eslint-disable */
import { ref, computed, watch, onMounted, onUnmounted } from 'vue'

const props = defineProps({
  images: {
    type: Array,
    required: true,
    default: () => []
  },
  // If only single image (backward compat)
  imageUrl: {
    type: String,
    default: null
  }
})

const lightboxOpen = ref(false)
const currentIndex = ref(0)

// Combine single imageUrl with images array
const allImages = computed(() => {
  if (props.images && props.images.length > 0) {
    return props.images
  }
  if (props.imageUrl) {
    return [props.imageUrl]
  }
  return []
})

// For grid display, show max 4 images
const displayImages = computed(() => {
  return allImages.value.slice(0, 4)
})

const remainingCount = computed(() => {
  return Math.max(0, allImages.value.length - 4)
})

// Dynamic grid class based on image count
const gridClass = computed(() => {
  const count = Math.min(allImages.value.length, 4)
  return `grid-${count}`
})

const openLightbox = (index) => {
  currentIndex.value = index
  lightboxOpen.value = true
  document.body.style.overflow = 'hidden'
}

const closeLightbox = () => {
  lightboxOpen.value = false
  document.body.style.overflow = ''
}

const prevImage = () => {
  if (currentIndex.value > 0) {
    currentIndex.value--
  }
}

const nextImage = () => {
  if (currentIndex.value < allImages.value.length - 1) {
    currentIndex.value++
  }
}

const handleImageError = (e) => {
  console.error('Image failed to load:', allImages.value[currentIndex.value])
}

// Keyboard navigation
const handleKeydown = (e) => {
  if (!lightboxOpen.value) return
  
  switch (e.key) {
    case 'Escape':
      closeLightbox()
      break
    case 'ArrowLeft':
      prevImage()
      break
    case 'ArrowRight':
      nextImage()
      break
  }
}

onMounted(() => {
  window.addEventListener('keydown', handleKeydown)
})

onUnmounted(() => {
  window.removeEventListener('keydown', handleKeydown)
  document.body.style.overflow = ''
})

// Expose for parent components
defineExpose({
  openLightbox,
  closeLightbox
})
</script>

<style scoped>
/* Album Grid Styles */
.album-grid {
  display: grid;
  gap: 4px;
  border-radius: 8px;
  overflow: hidden;
}

.grid-1 {
  grid-template-columns: 1fr;
  max-width: 400px;
}

.grid-1 .album-image {
  max-height: 300px;
}

.grid-1 .album-image :deep(.v-img) {
  max-height: 300px;
}

.grid-2 {
  grid-template-columns: 1fr 1fr;
  max-height: 250px;
}

.grid-3 {
  grid-template-columns: 1fr 1fr;
  grid-template-rows: 1fr 1fr;
  max-height: 300px;
}

.grid-3 .album-image:first-child {
  grid-row: span 2;
}

.grid-4 {
  grid-template-columns: 1fr 1fr;
  grid-template-rows: 1fr 1fr;
  max-height: 300px;
}

.album-image {
  position: relative;
  cursor: pointer;
  overflow: hidden;
}

.album-image:hover .v-img {
  transform: scale(1.05);
  transition: transform 0.2s ease;
}

.album-image:hover .zoom-hint {
  opacity: 1;
}

.zoom-hint {
  position: absolute;
  bottom: 8px;
  right: 8px;
  background: rgba(0, 0, 0, 0.5);
  border-radius: 50%;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0;
  transition: opacity 0.2s ease;
  pointer-events: none;
}

.has-overlay .v-img {
  filter: brightness(0.7);
}

.overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(0, 0, 0, 0.4);
  pointer-events: none;
}

.overlay-text {
  color: white;
  font-size: 1.5rem;
  font-weight: bold;
}

.cursor-pointer {
  cursor: pointer;
}

/* Lightbox Styles */
.lightbox {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.95);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  z-index: 9999;
}

.close-btn {
  position: absolute;
  top: 16px;
  right: 16px;
  z-index: 10;
}

.counter {
  position: absolute;
  top: 20px;
  left: 50%;
  transform: translateX(-50%);
  color: white;
  font-size: 1rem;
  background: rgba(0, 0, 0, 0.5);
  padding: 4px 12px;
  border-radius: 20px;
}

.main-image-container {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  flex: 1;
  padding: 60px 20px 20px;
}

.main-image {
  max-width: 90vw;
  max-height: 70vh;
  display: flex;
  align-items: center;
  justify-content: center;
}

.lightbox-img {
  max-width: 90vw;
  max-height: 70vh;
  object-fit: contain;
  border-radius: 4px;
}

.nav-btn {
  position: absolute;
  z-index: 10;
}

.nav-prev {
  left: 16px;
}

.nav-next {
  right: 16px;
}

/* Thumbnail Strip */
.thumbnail-strip {
  width: 100%;
  padding: 16px;
  background: rgba(0, 0, 0, 0.5);
  overflow-x: auto;
}

.thumbnail-container {
  display: flex;
  gap: 8px;
  justify-content: center;
  min-width: min-content;
}

.thumbnail {
  width: 60px;
  height: 60px;
  border-radius: 4px;
  overflow: hidden;
  cursor: pointer;
  opacity: 0.5;
  transition: opacity 0.2s, transform 0.2s;
  border: 2px solid transparent;
  flex-shrink: 0;
}

.thumbnail:hover {
  opacity: 0.8;
}

.thumbnail.active {
  opacity: 1;
  border-color: white;
  transform: scale(1.1);
}

/* Mobile adjustments */
@media (max-width: 600px) {
  .thumbnail {
    width: 48px;
    height: 48px;
  }
  
  .nav-btn {
    display: none;
  }
  
  .main-image-container {
    padding: 60px 10px 10px;
  }

  .zoom-hint {
    opacity: 0.7;
  }

  .grid-1 {
    max-width: 100%;
  }
}
</style>